import { motion } from "framer-motion";
import { Globe2, MapPin } from "lucide-react";

const Coverage = () => {
  return (
    <section
      id="coverage"
      className="relative py-20 sm:py-24 lg:py-28 overflow-hidden"
      style={{ background: "var(--bg)" }}
      aria-label="Where TAFRA Energy operates"
      data-testid="coverage-section"
    >
      <div className="absolute inset-0 energy-grid-bg opacity-[0.18] pointer-events-none" />
      <div className="absolute inset-0 radial-fade opacity-50" />
      <div className="absolute inset-0 radial-crimson opacity-60" />
      <span
        aria-hidden
        className="absolute top-0 left-0 right-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent, var(--accent-blue), transparent)",
          opacity: 0.4,
        }}
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ y: 18, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <span className="eyebrow">Where we work</span>
            <h2
              className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light leading-[1.05]"
              style={{ color: "var(--text)" }}
            >
              Operating across{" "}
              <span className="font-medium">all of Kenya</span>{" "}
              <span style={{ color: "var(--accent-blue)" }}>—</span>{" "}
              <br className="hidden sm:block" />
              <span className="font-medium">exploring the African horizon.</span>
            </h2>
            <p
              className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed"
              style={{ color: "var(--text-muted)" }}
            >
              From rooftop solar installations and full MEP build-outs in
              Nairobi to industrial-scale engineering anywhere in the 47
              counties — we dispatch nationwide, with our eyes on the wider
              African horizon.
            </p>

            <div className="mt-8 flex flex-wrap gap-3 font-mono-spec text-[11px] tracking-[0.25em] uppercase">
              <Pill icon={MapPin} accent="var(--accent-blue)">Kenya · Nationwide</Pill>
              <Pill icon={Globe2} accent="var(--accent-crimson-hover)">African Horizon</Pill>
            </div>
          </motion.div>

          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-5 relative flex items-center justify-center"
          >
            <CoverageMark />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Pill = ({ icon: Icon, children, accent }) => (
  <span
    className="inline-flex items-center gap-2 px-3 py-1.5 btn-sharp"
    style={{
      border: `1px solid ${accent}`,
      background: "var(--surface-glass)",
      color: accent,
    }}
  >
    <Icon className="w-3.5 h-3.5" strokeWidth={1.8} />
    {children}
  </span>
);

// A small concentric-radar mark that visually represents reach
const CoverageMark = () => (
  <div className="relative w-64 h-64 sm:w-80 sm:h-80">
    {[0, 1, 2, 3].map((i) => (
      <span
        key={i}
        className="absolute inset-0 rounded-full"
        style={{
          border: "1px solid var(--accent-blue)",
          opacity: 0.18 - i * 0.04,
          transform: `scale(${1 - i * 0.18})`,
        }}
      />
    ))}
    <span
      className="absolute inset-0 animate-pulse-glow rounded-full"
      style={{
        background:
          "radial-gradient(circle at 50% 50%, var(--accent-blue-glow), transparent 65%)",
      }}
    />
    {/* Central pin */}
    <span
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full"
      style={{
        background: "var(--accent-blue)",
        boxShadow: "0 0 18px var(--accent-blue-glow)",
      }}
    />
    {/* Secondary pins around the rings */}
    {[
      { x: "32%", y: "26%", c: "var(--accent-blue)" },
      { x: "72%", y: "38%", c: "var(--accent-blue)" },
      { x: "60%", y: "70%", c: "var(--accent-blue)" },
      { x: "26%", y: "62%", c: "var(--accent-crimson-hover)" },
      { x: "82%", y: "62%", c: "var(--accent-crimson-hover)" },
    ].map((p) => (
      <span
        key={`pin-${p.x}-${p.y}`}
        className="absolute w-2 h-2 rounded-full"
        style={{
          left: p.x,
          top: p.y,
          background: p.c,
          boxShadow: `0 0 12px ${p.c}`,
        }}
      />
    ))}
    {/* Compass labels */}
    <span
      className="absolute font-mono-spec text-[10px] tracking-[0.3em] uppercase"
      style={{
        top: "8%",
        left: "50%",
        transform: "translateX(-50%)",
        color: "var(--text-faint)",
      }}
    >
      Nairobi · HQ
    </span>
    <span
      className="absolute font-mono-spec text-[10px] tracking-[0.3em] uppercase"
      style={{
        bottom: "8%",
        left: "50%",
        transform: "translateX(-50%)",
        color: "var(--text-faint)",
      }}
    >
      Nationwide · KE
    </span>
  </div>
);

export default Coverage;
